import { endpoints } from '@/api/endpoints';
import type {
  ChannelType,
  Message,
  MessageMediaType,
  OmnichannelApiMessageType,
  OmnichannelMessage,
  OmnichannelOutboundMessageBody,
  SendMessagePayload,
} from '@/types';
import { formatMessageTime } from '@/lib/helpers';

const API_ROOT = `${import.meta.env.VITE_BASE_URL ?? ''}/v1`;

/** True when `src` targets GET /omnichannel/media/... (WhatsApp — needs credentialed fetch). */
export function isOmnichannelProxyMediaUrl(url: string): boolean {
  return url.includes('/omnichannel/media/');
}

/** WhatsApp media proxy; Meta channels use direct URLs in `content`. */
export function getOmnichannelMediaSrc(
  channel: ChannelType,
  conversationId: string,
  content: string
): string {
  if (!content?.trim()) return '';
  if (channel === 'facebook' || channel === 'instagram') {
    return content;
  }
  const encoded = encodeURIComponent(content);
  const conv = encodeURIComponent(conversationId);
  return `${API_ROOT}${endpoints.omnichannel.media}/${encoded}?conversationId=${conv}`;
}

export function uiMediaTypeFromApi(
  messageType?: string | null
): MessageMediaType {
  switch (messageType) {
    case 'image':
    case 'sticker':
      return messageType;
    case 'video':
      return 'video';
    case 'audio':
      return 'audio';
    case 'voice_note':
      return 'voice';
    case 'document':
      return 'document';
    case 'file':
      return 'file';
    default:
      return 'text';
  }
}

function labelForMediaType(
  mediaType: MessageMediaType,
  fileName?: string
): string {
  switch (mediaType) {
    case 'image':
      return '[Image]';
    case 'video':
      return '[Video]';
    case 'audio':
      return '[Audio]';
    case 'voice':
      return '[Voice message]';
    case 'document':
    case 'file':
      return fileName ?? '[Document]';
    case 'sticker':
      return '[Sticker]';
    default:
      return '';
  }
}

function parseLegacyAttachments(attachments: unknown): {
  mediaType: MessageMediaType;
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
} | null {
  if (!attachments) return null;

  const first = Array.isArray(attachments)
    ? attachments[0]
    : typeof attachments === 'object' &&
        attachments !== null &&
        'data' in attachments &&
        Array.isArray((attachments as { data: unknown[] }).data)
      ? (attachments as { data: unknown[] }).data[0]
      : attachments;

  if (!first || typeof first !== 'object') return null;

  const att = first as Record<string, unknown>;
  const mimeType =
    typeof att.mime_type === 'string'
      ? att.mime_type
      : typeof att.mimetype === 'string'
        ? att.mimetype
        : undefined;

  const pickUrl = (...vals: unknown[]): string | undefined => {
    for (const v of vals) {
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (v && typeof v === 'object' && 'url' in v) {
        const u = (v as { url?: string }).url;
        if (typeof u === 'string' && u.trim()) return u.trim();
      }
    }
    return undefined;
  };

  const attType = typeof att.type === 'string' ? att.type.toLowerCase() : '';
  /** REST: `{ type, url }` — Meta/IG list: `{ type, payload: { url } }` */
  const resolvedUrl = pickUrl(
    att.url,
    att.payload,
    att.image_data,
    att.image_url
  );

  if (resolvedUrl && attType) {
    switch (attType) {
      case 'image':
        return { mediaType: 'image', mediaUrl: resolvedUrl, mimeType };
      case 'sticker':
        return { mediaType: 'sticker', mediaUrl: resolvedUrl, mimeType };
      case 'video':
        return { mediaType: 'video', mediaUrl: resolvedUrl, mimeType };
      case 'audio':
        return { mediaType: 'audio', mediaUrl: resolvedUrl, mimeType };
      case 'voice_note':
        return { mediaType: 'voice', mediaUrl: resolvedUrl, mimeType };
      case 'file':
        return {
          mediaType: 'file',
          mediaUrl: resolvedUrl,
          mimeType,
          fileName: typeof att.name === 'string' ? att.name : undefined,
        };
      case 'document':
        return {
          mediaType: 'document',
          mediaUrl: resolvedUrl,
          mimeType,
          fileName: typeof att.name === 'string' ? att.name : undefined,
        };
      default:
        break;
    }
  }

  const imageUrl = pickUrl(att.image_data, att.image_url, resolvedUrl);
  if (imageUrl || mimeType?.startsWith('image/')) {
    return { mediaType: 'image', mediaUrl: imageUrl, mimeType };
  }

  const audioUrl = pickUrl(att.audio_data, att.audio_url, att.file_url);
  if (audioUrl || mimeType?.startsWith('audio/')) {
    return {
      mediaType: mimeType?.includes('ogg') ? 'voice' : 'audio',
      mediaUrl: audioUrl,
      mimeType,
      fileName: typeof att.name === 'string' ? att.name : undefined,
    };
  }

  const fileUrl = pickUrl(att.file_url, resolvedUrl);
  if (fileUrl) {
    return {
      mediaType: mimeType?.startsWith('video/') ? 'video' : 'document',
      mediaUrl: fileUrl,
      fileName: typeof att.name === 'string' ? att.name : undefined,
      mimeType,
    };
  }

  return null;
}

export function previewLabelFromAttachments(
  attachments: unknown
): string | null {
  const parsed = parseLegacyAttachments(attachments);
  if (!parsed) return null;
  return labelForMediaType(parsed.mediaType, parsed.fileName);
}

/** Meta often duplicates the CDN URL in `message` when `attachments` hold the real media. */
function shouldHideUrlCaption(
  text: string | undefined,
  mediaType: MessageMediaType,
  mediaUrl: string | undefined
): boolean {
  const t = text?.trim();
  if (!t) return false;
  if (mediaUrl && t === mediaUrl.trim()) return true;
  if (
    mediaType !== 'text' &&
    mediaType !== 'document' &&
    mediaType !== 'file' &&
    /^https?:\/\//i.test(t)
  ) {
    return true;
  }
  return false;
}

export function mapApiMessageToUi(
  msg: OmnichannelMessage,
  channel: ChannelType,
  conversationId: string,
  businessId?: string,
  initials?: { customerInitials?: string; businessInitials?: string }
): Message {
  const senderId = msg.senderId ?? msg.from?.id;
  const isOutgoing = businessId
    ? senderId === businessId || msg.from?.id === businessId
    : false;

  const attachmentMedia = parseLegacyAttachments(msg.attachments);

  const apiType =
    msg.messageType ??
    (attachmentMedia
      ? attachmentMedia.mediaType === 'voice'
        ? 'voice_note'
        : attachmentMedia.mediaType
      : null);
  let mediaType = apiType ? uiMediaTypeFromApi(apiType) : 'text';
  let mediaUrl: string | undefined;
  let fileName: string | undefined;
  let mimeType: string | undefined;

  const content =
    typeof msg.content === 'string'
      ? msg.content
      : typeof msg.message === 'string'
        ? msg.message
        : '';

  if (attachmentMedia?.mediaUrl) {
    mediaType = attachmentMedia.mediaType;
    mediaUrl = attachmentMedia.mediaUrl.startsWith('http')
      ? attachmentMedia.mediaUrl
      : getOmnichannelMediaSrc(
          channel,
          conversationId,
          attachmentMedia.mediaUrl
        );
    fileName = attachmentMedia.fileName;
    mimeType = attachmentMedia.mimeType;
  } else if (mediaType !== 'text' && content) {
    mediaUrl = getOmnichannelMediaSrc(channel, conversationId, content);
  }

  const attRecord = Array.isArray(msg.attachments)
    ? (msg.attachments[0] as
        | { mime_type?: string; filename?: string; name?: string }
        | undefined)
    : (msg.attachments as
        | { mime_type?: string; filename?: string; name?: string }
        | undefined);
  if (attRecord?.mime_type) mimeType = attRecord.mime_type;
  if (attRecord?.filename) fileName = attRecord.filename;
  if (attRecord?.name) fileName = attRecord.name;

  const rawCaption = msg.message?.trim() || content?.trim() || '';
  const displayMessage =
    mediaType === 'text'
      ? content || msg.message || ''
      : shouldHideUrlCaption(rawCaption, mediaType, mediaUrl)
        ? labelForMediaType(mediaType, fileName)
        : rawCaption || labelForMediaType(mediaType, fileName);

  let sender: string;
  if (isOutgoing) {
    sender =
      initials?.businessInitials ??
      (msg.from?.name ?? 'You').substring(0, 2).toUpperCase();
  } else {
    sender =
      initials?.customerInitials ??
      (msg.from?.name ?? 'User').substring(0, 2).toUpperCase();
  }

  const createdAt = msg.createdAt ?? msg.created_time;

  return {
    id: msg.id,
    sender,
    message: displayMessage,
    time: formatMessageTime(createdAt),
    isOutgoing,
    createdAt,
    mediaType,
    mediaUrl,
    fileName,
    mimeType,
    apiMessageType: apiType ?? undefined,
  };
}

/** Maps internal send payload to the API message body (path param: conversationId). */
export function toOmnichannelMessageBody(
  payload: SendMessagePayload
): OmnichannelOutboundMessageBody {
  const channel = payload.channel.toLowerCase() as ChannelType;
  const body: OmnichannelOutboundMessageBody = {
    channel,
    to: payload.to,
    pageId: payload.pageId,
    messageType: payload.messageType,
  };

  if (payload.messageType === 'text') {
    body.message = payload.message ?? '';
    return body;
  }

  if (!payload.attachment?.url) {
    throw new Error('Media messages require attachment.url');
  }

  body.attachment = {
    type: payload.messageType,
    url: payload.attachment.url,
  };

  if (
    payload.message?.trim() &&
    supportsCaptionOnMedia(channel, payload.messageType)
  ) {
    body.message = payload.message.trim();
  }

  return body;
}

function supportsCaptionOnMedia(
  channel: ChannelType,
  messageType: OmnichannelApiMessageType
): boolean {
  if (channel !== 'whatsapp') return false;
  return (
    messageType === 'image' ||
    messageType === 'video' ||
    messageType === 'document'
  );
}

/** Build one or more API payloads after upload (Meta: caption = separate text message). */
export function buildOutboundMediaPayloads(params: {
  conversationId: string;
  channel: ChannelType;
  to: string;
  pageId: string;
  uploadUrl: string;
  uiMediaType: 'image' | 'document' | 'voice';
  caption?: string;
}): SendMessagePayload[] {
  const {
    conversationId,
    channel,
    to,
    pageId,
    uploadUrl,
    uiMediaType,
    caption,
  } = params;

  const base = { conversationId, channel, to, pageId };

  let messageType: OmnichannelApiMessageType;

  if (uiMediaType === 'image') {
    messageType = 'image';
  } else if (uiMediaType === 'voice') {
    messageType = channel === 'whatsapp' ? 'voice_note' : 'audio';
  } else if (channel === 'whatsapp') {
    messageType = 'document';
  } else {
    messageType = 'file';
  }

  const payloads: SendMessagePayload[] = [
    {
      ...base,
      messageType,
      attachment: {
        type: messageType,
        url: uploadUrl,
      },
      ...(supportsCaptionOnMedia(channel, messageType) && caption?.trim()
        ? { message: caption.trim() }
        : {}),
    },
  ];

  // Meta: captions only as a separate text message for image (not document/file).
  const captionText = caption?.trim();
  const metaNeedsSeparateCaption =
    channel === 'facebook' || channel === 'instagram';
  if (captionText && metaNeedsSeparateCaption && uiMediaType === 'image') {
    payloads.push({
      ...base,
      messageType: 'text',
      message: captionText,
    });
  }

  return payloads;
}
